import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { SubscriptionService } from '../Services/SubscriptionService';
import { FreeGenerationService } from '../Services/FreeGenerationService';
import { StorageUtils } from '../Utils/StorageUtils';

const UserStatusContext = createContext();

export const UserStatusProvider = ({ children }) => {
  // Initialize user status - default to 'starter' 
  const [userStatus, setUserStatus] = useState(() => {
    const saved = localStorage.getItem('userStatus');
    return saved ? JSON.parse(saved) : 'starter';
  });

  // Loading state for subscription sync
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  
  // Loading state for free generation sync
  const [isLoadingGenerations, setIsLoadingGenerations] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(() => {
    return localStorage.getItem('lastSubscriptionSync') || null;
  });

  // Initialize remaining link generations - start with 0, will be synced from backend
  const [remainingLinks, setRemainingLinks] = useState(0);

  // Track upgrade celebration state
  const [showUpgradeCelebration, setShowUpgradeCelebration] = useState(false);
  const previousUserStatusRef = useRef(userStatus);

  // Persist user status to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('userStatus', JSON.stringify(userStatus));
  }, [userStatus]);

  // Note: remainingLinks is now synced from backend, not persisted to localStorage

  // Detect user status upgrade and trigger celebration
  useEffect(() => {
    const prevStatus = previousUserStatusRef.current;
    const currentStatus = userStatus;
    
    // Only trigger celebration on genuine upgrade (not initial sync)
    if (prevStatus === 'starter' && currentStatus === 'pro' && prevStatus !== currentStatus) {
      console.log('🎉 User upgraded from starter to pro! Triggering celebration...');
      setShowUpgradeCelebration(true);
    }
    
    // Update the ref for next comparison
    previousUserStatusRef.current = currentStatus;
  }, [userStatus]);

  // Sync subscription status from server on app load
  useEffect(() => {
    const userData = StorageUtils.getUserData();
    if (userData?.token) {
      console.log('🚀 Syncing subscription status on app load...');
      syncSubscriptionStatus(false); // Don't force on initial load
      console.log('🚀 Syncing free generation status on app load...');
      syncFreeGenerationStatus(); // Always fetch fresh data
    }
  }, []); // Run once on mount

  // Sync when window regains focus (user switches between tabs/windows)
  useEffect(() => {
    const handleFocus = () => {
      const userData = StorageUtils.getUserData();
      if (userData?.token) {
        console.log('🔄 Window focused, syncing free generation status...');
        syncFreeGenerationStatus(); // Always fetch fresh data
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []); // Run once on mount

  // Listen for userDataRefresh event (dispatched on login/signup)
  useEffect(() => {
    const handleUserDataRefresh = () => {
      console.log('🔄 userDataRefresh event detected - refreshing all user data...');
      forceRefreshAllUserData();
    };

    window.addEventListener('userDataRefresh', handleUserDataRefresh);
    return () => window.removeEventListener('userDataRefresh', handleUserDataRefresh);
  }, []); // Run once on mount

  // Toggle between starter and pro (for debug purposes)
  const toggleUserStatus = () => {
    setUserStatus(prev => prev === 'starter' ? 'pro' : 'starter');
  };

  // Set user status explicitly
  const setStatus = (status) => {
    if (status === 'starter' || status === 'pro') {
      setUserStatus(status);
      
      // Track when the status was updated locally
      localStorage.setItem('lastStatusUpdate', Date.now().toString());
      console.log('📦 Local status updated to:', status, 'at', new Date().toISOString());
      
      // Also update server status asynchronously
      updateServerStatus(status);
    }
  };

  // Manual decrement for testing (separate from actual link creation)
  const manualDecrementRemainingLinks = async () => {
    if (userStatus === 'starter' && remainingLinks > 0) {
      try {
        console.log('🔧 Debug: Calling backend to decrement generation count...');
        const response = await FreeGenerationService.decrementUserGeneration();
        const data = response?.data || response;
        
        if (data.success) {
          console.log('🔧 Debug: Successfully decremented generation count:', data);
          setRemainingLinks(data.remaining);
        } else {
          console.warn('🔧 Debug: Failed to decrement:', data.message);
        }
        return data.success;
      } catch (error) {
        console.error('🔧 Debug: Error decrementing generation count:', error);
        // Fallback to local decrement if API fails
        setRemainingLinks(prev => prev - 1);
        return true;
      }
    }
    return false;
  };

  // Decrement remaining links (when a link is created)
  const decrementRemainingLinks = () => {
    if (userStatus === 'starter' && remainingLinks > 0) {
      setRemainingLinks(prev => prev - 1);
      return true; // Link creation allowed
    } else if (userStatus === 'pro') {
      return true; // Pro users have unlimited links
    }
    return false; // No links remaining for starter users
  };

  // Reset remaining links count (for start of new month)
  const resetRemainingLinks = async () => {
    try {
      console.log('🔧 Debug: Calling backend to reset generation count...');
      const response = await FreeGenerationService.resetUserGeneration();
      const data = response?.data || response;
      
      if (data.success) {
        console.log('🔧 Debug: Successfully reset generation count:', data);
        setRemainingLinks(data.remaining);
      } else {
        console.warn('🔧 Debug: Failed to reset:', data.message);
      }
    } catch (error) {
      console.error('🔧 Debug: Error resetting generation count:', error);
      // Fallback to local reset if API fails
      if (userStatus === 'starter') {
        setRemainingLinks(2);
      }
    }
  };

  // Check if user can create links
  const canCreateLinks = () => {
    return userStatus === 'pro' || remainingLinks > 0;
  };

  // Get display text for remaining links
  const getRemainingLinksText = () => {
    if (userStatus === 'pro') {
      return 'Unlimited';
    }
    return remainingLinks.toString();
  };

  // Get user plan display name
  const getPlanDisplayName = () => {
    return userStatus === 'pro' ? 'Pro Plan' : 'Starter Plan';
  };

  // Handle closing the upgrade celebration
  const closeUpgradeCelebration = () => {
    setShowUpgradeCelebration(false);
  };

  // Sync subscription status from server
  const syncSubscriptionStatus = async (forceSync = false) => {
    const userData = StorageUtils.getUserData();
    if (!userData?.token) {
      console.log('🔒 No auth token, skipping subscription sync');
      return;
    }

    // Check if we need to sync (avoid excessive API calls)
    const now = Date.now();
    const syncInterval = 5 * 60 * 1000; // 5 minutes
    if (!forceSync && lastSyncTime && (now - parseInt(lastSyncTime)) < syncInterval) {
      console.log('🕐 Subscription sync skipped - too recent');
      return;
    }

    setIsLoadingSubscription(true);
    
    try {
      const response = await SubscriptionService.getUserStatus();
      const serverStatus = response?.data?.userStatus || response?.userStatus || response?.data?.status;
      
      if (serverStatus && (serverStatus === 'starter' || serverStatus === 'pro')) {
        const currentLocalStatus = userStatus;
        
        console.log('🔄 Server status:', serverStatus, '| Local status:', currentLocalStatus);
        
        // Always update to server status - no more downgrade prevention
        console.log('🔄 Synced subscription status from server:', serverStatus);
        setUserStatus(serverStatus);
        
        // Update both sync and status update timestamps
        const syncTime = now.toString();
        setLastSyncTime(syncTime);
        localStorage.setItem('lastSubscriptionSync', syncTime);
        localStorage.setItem('lastStatusUpdate', syncTime);
      }
    } catch (error) {
      console.error('❌ Failed to sync subscription status:', error);
      // Keep using localStorage status as fallback
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  // Update subscription status on server when changed locally
  const updateServerStatus = async (status) => {
    const userData = StorageUtils.getUserData();
    if (!userData?.token) return;

    try {
      await SubscriptionService.updateSubscriptionStatus(status);
      console.log('✅ Updated subscription status on server:', status);
    } catch (error) {
      console.error('❌ Failed to update server subscription status:', error);
      // Continue with local update - server will sync later
    }
  };

  // Get detailed subscription information
  const getSubscriptionDetails = async () => {
    const userData = StorageUtils.getUserData();
    if (!userData?.token) {
      console.log('🔒 No auth token, cannot get subscription details');
      return null;
    }

    try {
      const response = await SubscriptionService.getSubscriptionDetails();
      const details = response?.data || response;
      
      // Update local status from detailed response
      if (details?.userStatus && (details.userStatus === 'starter' || details.userStatus === 'pro')) {
        setUserStatus(details.userStatus);
      }
      
      console.log('📋 Got subscription details:', details);
      return details;
    } catch (error) {
      console.error('❌ Failed to get subscription details:', error);
      return null;
    }
  };

  // Sync subscription data from Stripe (for troubleshooting)
  const syncFromStripe = async () => {
    const userData = StorageUtils.getUserData();
    if (!userData?.token) {
      console.log('🔒 No auth token, cannot sync from Stripe');
      return;
    }

    setIsLoadingSubscription(true);
    
    try {
      const response = await SubscriptionService.syncSubscriptionFromStripe();
      const result = response?.data || response;
      
      if (result?.userStatus && (result.userStatus === 'starter' || result.userStatus === 'pro')) {
        console.log('🔄 Synced from Stripe:', result.userStatus);
        setUserStatus(result.userStatus);
        
        // Update sync timestamp
        const syncTime = Date.now().toString();
        setLastSyncTime(syncTime);
        localStorage.setItem('lastSubscriptionSync', syncTime);
      }
      
      // Refresh the full status after Stripe sync
      await syncSubscriptionStatus(true);
      
      return result;
    } catch (error) {
      console.error('❌ Failed to sync from Stripe:', error);
      throw error;
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  // Force refresh of user status (useful after payments)
  const refreshUserStatus = async () => {
    console.log('🔄 Force refreshing user status...');
    await syncSubscriptionStatus(true);
  };

  // Force refresh all user data (useful after login with new user)
  const forceRefreshAllUserData = async () => {
    console.log('🎆 Force refreshing ALL user data after login...');
    
    // Reset to defaults first
    setUserStatus('starter');
    setRemainingLinks(0); // Will be synced from backend
    setLastSyncTime(null);
    
    // Reset celebration state to prevent false celebrations on login
    setShowUpgradeCelebration(false);
    
    // Clear localStorage cache
    localStorage.removeItem('userStatus');
    localStorage.removeItem('lastSubscriptionSync');
    localStorage.removeItem('lastStatusUpdate');
    
    // Force sync from server
    await syncSubscriptionStatus(true);
    await syncFreeGenerationStatus();
    
    console.log('✨ User data refresh complete');
  };

  // Sync free generation status from server (no caching - always fresh data)
  const syncFreeGenerationStatus = async () => {
    const userData = StorageUtils.getUserData();
    if (!userData?.token) {
      console.log('🔒 No auth token, skipping free generation sync');
      return;
    }

    setIsLoadingGenerations(true);
    
    try {
      console.log('🔄 Syncing free generation status from server...');
      const response = await FreeGenerationService.getGenerationStatus();
      const data = response?.data || response;
      
      if (data && typeof data.remaining === 'number') {
        console.log('🔄 Synced free generation status:', data);
        setRemainingLinks(data.remaining);
        
        // Also update generation config if provided
        if (data.resetAt) {
          // You can store reset time in state if needed
          console.log('📅 Next reset at:', data.resetAt);
        }
      }
    } catch (error) {
      console.error('❌ Failed to sync free generation status:', error);
      // Keep using current value as fallback
    } finally {
      setIsLoadingGenerations(false);
    }
  };

  // Check if user is on pro plan
  const isPro = () => {
    return userStatus === 'pro';
  };

  // Check if user is on starter plan  
  const isStarter = () => {
    return userStatus === 'starter';
  };

  // Force sync subscription status (bypasses cache)
  const forceSyncSubscriptionStatus = async () => {
    return await syncSubscriptionStatus(true);
  };

  const value = {
    // Status values
    userStatus,
    remainingLinks,
    showUpgradeCelebration,
    isLoadingSubscription,
    isLoadingGenerations,
    
    // Helper functions
    isPro,
    isStarter,
    canCreateLinks,
    getRemainingLinksText,
    getPlanDisplayName,
    
    // Legacy methods (kept for compatibility)
    toggleUserStatus,
    setStatus,
    decrementRemainingLinks,
    manualDecrementRemainingLinks,
    resetRemainingLinks,
    closeUpgradeCelebration,
    
    // New enhanced methods
    syncSubscriptionStatus,
    syncFreeGenerationStatus,
    getSubscriptionDetails,
    syncFromStripe,
    refreshUserStatus,
    forceRefreshAllUserData,
    forceSyncSubscriptionStatus,
  };

  return (
    <UserStatusContext.Provider value={value}>
      {children}
    </UserStatusContext.Provider>
  );
};

export const useUserStatus = () => {
  const context = useContext(UserStatusContext);
  if (context === undefined) {
    throw new Error('useUserStatus must be used within a UserStatusProvider');
  }
  return context;
};